import {Injectable} from '@angular/core';
import {AlertController, Platform} from '@ionic/angular';
import {environment} from '../../environments/environment';
import {IObjectMap} from '../models/IObjectMap';
import {from, Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import * as Sentry from '@sentry/browser';
import {BrowserService} from './browser.service';

export interface IVersion {
  version: string;
  buildTime: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppVersionService {
  public appVersion: IVersion;
  public webVersion: IVersion;

  INVITE_TO_STORE_ALERT_TEXT: IObjectMap<string> = {
    header: 'Install App',
    content: 'Safe Check-In',
    positiveAction: 'INSTALL',
    cancelAction: 'NOT NOW'
  };

  constructor(
    private platform: Platform,
    private alertController: AlertController,
    private browserService: BrowserService,
    private http: HttpClient,
    private ts: TranslateService
  ) {
    this.initVersion();
  }

  public displayShortcutAppAlert() {
    if (this.isAndroid()) {
      this.shortcutAndroidAppAlert();
    } else if (this.isIos()) {
      this.shortcutIosAppAlert();
    }
  }

  public getAppVersion(): Observable<IVersion> {
    if (this.appVersion) {
      return from([this.appVersion]);
    }
    return this.http.get<IVersion>(`assets/version.json`);
  }

  public getWebVersion(): Observable<IVersion> {
    if (this.webVersion) {
      return from([this.webVersion]);
    }
    return this.http.get<IVersion>(environment.webVersion, {
      headers: {
        errors: 'silent'
      }
    });
  }

  public isMobileWeb() {
    return this.platform.is('mobileweb');
  }

  public isMobile() {
    return this.platform.is('mobile');
  }

  public isIos() {
    return this.platform.is('ios');
  }

  public isAndroid() {
    return this.platform.is('android');
  }

  async inviteToStore() {
    const alert = await this.alertController.create({
      mode: 'ios',
      cssClass: 'alert-custom-wrapper',
      header: this.ts.instant(this.INVITE_TO_STORE_ALERT_TEXT.header),
      message: `<div class="alert-content-align"><img src="../../../../assets/imgs/logo.png" alt="logo" class="logo-img alert-img-radius"></div><div class="alert-text"><b>${this.ts.instant(this.INVITE_TO_STORE_ALERT_TEXT.content)}</b></div>`,
      buttons: [{
        text: this.ts.instant(this.INVITE_TO_STORE_ALERT_TEXT.cancelAction),
        cssClass: 'secondary',
        role: 'cancel',
        handler: () => {
        }
      }, {
        text: this.ts.instant(this.INVITE_TO_STORE_ALERT_TEXT.positiveAction),
        handler: () => this.goToStore()
      }]
    });

    await alert.present();
  }

  async updateAppAlert() {
    const alert = await this.alertController.create({
      header: this.ts.instant('Your app is out of date, please update'),
      buttons: [{
        text: this.ts.instant('Cancel'),
        cssClass: 'secondary',
        role: 'cancel',
        handler: () => {
        }
      }, {
        text: this.ts.instant('Update'),
        handler: () => this.goToStore()
      }]
    });

    await alert.present();
  }

  private async presentShortcutAlert(header: string, message: string): Promise<void> {
    const alert = await this.alertController.create({
      header: this.ts.instant(header),
      message,
      buttons: [{
        text: this.ts.instant('Ok'),
        handler: () => this.setHomeScreenInfoRead()
      }]
    });
    await alert.present();
  }

  /**
   * Shows the iOS-specific alert for adding an icon to the home screen.
   */
  public async shortcutIosAppAlert(): Promise<void> {
    const message = `
      <div class="share-text">
        <b>${this.ts.instant('Click export icon:')}</b>
        <img src="../../../../assets/imgs/export-icon.png" alt="logo" class="export-icon">
      </div>
      <div class="share-text">
        <b>${this.ts.instant('and')}</b>
      </div>
      <div class="share-text">
        <b>${this.ts.instant('Add to Home Screen:')}</b>
        <img src="../../../../assets/imgs/export-home-screen.svg" alt="logo" class="export-home-screen">
      </div>
    `;

    await this.presentShortcutAlert(
      'Add Safe-Checkin to Home screen',
      message
    );
  }

  /**
   * Shows the Android-specific alert for adding an icon to the home screen.
   */
  public async shortcutAndroidAppAlert(): Promise<void> {
    const message = `
      <div class="share-text">
        <b>${this.ts.instant('Click more button:')}</b>
        <img src="../../../../assets/imgs/android-more-vertical.svg" alt="logo" class="export-home-screen">
      </div>
      <div class="share-text">
        <b>${this.ts.instant('and')}</b>
      </div>
      <div class="share-text">
        <b>${this.ts.instant('Add to Home Screen:')}</b>
        <img src="../../../../assets/imgs/add-to-home-screen.png" alt="logo" class="export-home-screen">
      </div>
    `;

    await this.presentShortcutAlert(
      'Add Safe-Checkin to Home screen',
      message
    );
  }


  async setHomeScreenInfoRead() {
    localStorage.homeScreenInfoRead = true;
  }

  async goToStore() {
    if (this.isIos()) {
      const url = environment.appStoreUrl;
      this.browserService.open(url);
    } else {
      window.open(environment.androidMarketUrl, '_system');
    }
  }

  private async initVersion() {
    try {
      this.appVersion = await this.getAppVersion().toPromise();
      Sentry.setExtra('userAppVersion', this.appVersion);
      this.webVersion = await this.getWebVersion().toPromise();

      if (this.isMobile()) {
        if (this.cmpVersions(this.webVersion.version, this.appVersion.version) > 0) {
          this.updateAppAlert();
        }
      }
    } catch (e) {
      // console.warn(e);
    }
  }

  private cmpVersions(a, b) {
    let i, diff;
    const regExStrip0 = /(\.0+)+$/;
    const segmentsA = a.replace(regExStrip0, '').split('.');
    const segmentsB = b.replace(regExStrip0, '').split('.');
    const l = Math.min(segmentsA.length, segmentsB.length);

    for (i = 0; i < l; i++) {
      diff = parseInt(segmentsA[i], 10) - parseInt(segmentsB[i], 10);
      if (diff) {
        return diff;
      }
    }
    return segmentsA.length - segmentsB.length;
  }
}
